import React, { useState, useEffect } from 'react';
import {
  AdminContainer,
  AdminContainerCentered,
  AdminFlexRow,
  FlexRowButtonRow,
  StyledFormControl,
  FlexRowPad20,
} from './styles';
import { get, size, set } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Button, Checkbox, IconButton } from '@mui/material';
import { stopProp } from 'global/utils';
import { getRealmIcon } from 'global/components/droprealms';
import { withRouter } from 'react-router-dom';
import sendData from 'redux/actions/sendData';
import { Loader, useScreen } from 'global/styles';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import Alert from '@mui/material/Alert';
import { useSelector } from 'react-redux';
import { DropDown } from 'global/components/dropdown';
import copy from 'copy-to-clipboard';
import { IconButton as AppIconButton } from 'global/components/Button';
import { buildCreateFromTemplateUrl } from 'feature/report/AssessmentCreateFromTemplate/createFromTemplateConfig';
import { FlexGridDataTable, FlexGridIconLabel, FlexGridListHint } from 'global/components/flex-grid-list';
import SnackAlerts from 'global/components/snack-alerts';

const COPY_LINK_MSG = 'Link Copied!';

// PFM-2545: per-row copy-link button for the static create-from-template link.
// The clipboard write is synchronous (no await before copy()) so it stays
// iOS-safe. On desktop we flash "Link Copied!" inline; on mobile the parent
// shows a snackbar so the label is not clipped in the narrow Link column.
// stopProp keeps the row onClick (which opens the template editor) from also firing.
const CopyLinkButton = ({ templateId, onCopied }) => {
  const { isMobileXS } = useScreen();
  const [copied, setCopied] = useState(false);

  const onCopy = (e) => {
    stopProp(e);
    const url = buildCreateFromTemplateUrl(templateId);
    if (url) copy(url);
    if (isMobileXS) {
      onCopied?.(COPY_LINK_MSG);
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return copied ? (
    <span style={{ whiteSpace: 'nowrap' }}>{COPY_LINK_MSG}</span>
  ) : (
    <AppIconButton variant="text" color="secondary" toolTip="Copy create link" onClick={onCopy} icon={<LinkIcon fontSize="small" />} />
  );
};

export default withRouter((props) => {
  const { isMobile } = useScreen();
  const [error, setError] = useState();
  const [alerts, setAlerts] = useState();
  const [state, setState] = useState({
    scorecards: [],
    scValue: 0,
    status: [
      { title: 'Default', value: 'template' },
      { title: 'Global', value: 'global_template' },
    ],
    statusValue: 0,
    selected: {},
    loading: false,
  });
  const realm = useSelector((state) => get(state, 'auth.realm.id'));
  const templates = useRetrieveData(
    { url: '/assessment-templates' },
    {
      build: ['assessments'],
      useEffect: true,
      params: {
        include: 'scorecards,realm,authors',
        filter: realm && { realm },
      },
    }
  );

  const temps = get(templates, 'data', []);

  useEffect(() => {
    if (size(temps)) {
      const scorecards = {};
      temps.map((template) => {
        template.scorecards.map((sc) => {
          set(scorecards, get(sc, 'id'), sc);
        });
      });

      setState((s) => ({
        ...s,
        scorecards: Object.keys(scorecards).map((k) => ({ ...scorecards[k] })),
      }));
    }
  }, [temps]);

  const data = temps.filter((t) => {
    let passes = true;
    if (state.scValue) {
      passes = get(t, 'scorecards', []).find((sc) => sc.id === state.scValue);
    }

    if (passes && state.statusValue) {
      passes = get(t, 'status', '') === state.statusValue;
    }

    return passes;
  });

  const checkChange = (row, e) => {
    stopProp(e);
    const selected = { ...state.selected };
    const isSel = !get(selected, row.id);
    set(selected, row.id, isSel);
    setState((s) => ({ ...s, selected }));
  };

  const selectRow = (row) => {
    props.history.push(`/reports/assessment-template/${row.id}`, { from: 'account/content' });
  };

  const processSelected = async (status = 'template') => {
    try {
      setState((s) => ({ ...s, loading: true }));
      await Promise.all(
        Object.keys(state.selected)
          .filter((k) => state.selected[k])
          .map(async (k) => {
            const item = data.find((d) => d.id === k);
            if (item) {
              const id = get(item, 'id').toString();
              await sendData({
                method: 'patch',
                url: `/assessment-templates/${id}`,
                data: {
                  data: {
                    type: 'assessment-templates',
                    id,
                    attributes: {
                      status,
                    },
                  },
                },
              });
            }
          })
      );
      await templates.update();
    } catch (err) {
      setError(`Failed to update: ${err.message}`);
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const hasSelected = size(Object.keys(state.selected).filter((k) => !!state.selected[k]));

  return state.loading ? (
    <AdminContainerCentered isMobile={isMobile}>
      <Loader />
    </AdminContainerCentered>
  ) : (
    <AdminContainer isMobile={isMobile}>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <AdminFlexRow>
        <StyledFormControl>
          <DropDown
            label="Scorecard"
            value={state.scValue}
            onChange={(e) => setState((s) => ({ ...s, scValue: e.target.value }))}
            options={[{ id: 0, name: 'Any' }].concat(
              state.scorecards.map((sc) => ({
                id: get(sc, 'id'),
                name: get(sc, 'name'),
              }))
            )}
          />
        </StyledFormControl>

        <StyledFormControl>
          <DropDown
            label="Status"
            value={state.statusValue}
            onChange={(e) => setState((s) => ({ ...s, statusValue: e.target.value }))}
            options={[{ id: 0, name: 'Any' }].concat(
              state.status.map((sc) => ({
                id: sc.value,
                name: sc.title,
              }))
            )}
          />
        </StyledFormControl>
      </AdminFlexRow>

      {!!hasSelected && (
        <FlexRowButtonRow>
          <Button color="secondary" variant="contained" onClick={() => processSelected('global_template')}>
            MAKE GLOBAL
          </Button>
          <Button color="secondary" variant="contained" onClick={() => processSelected('template')}>
            REMOVE FROM GLOBAL
          </Button>
        </FlexRowButtonRow>
      )}

      {templates.loading ? (
        <FlexRowPad20 isMobile={isMobile}>
          <Loader />
        </FlexRowPad20>
      ) : (
        <>
          <FlexGridListHint />
          <FlexGridDataTable
            items={data}
            columns={[
              {
                field: '_check',
                title: '',
                width: '5%',
                minWidth: 42,
                cellSx: { overflow: 'visible' },
                render: (row) => (
                  <Checkbox checked={!!get(state, `selected.${row.id}`)} onClick={(e) => checkChange(row, e)} />
                ),
              },
              { field: 'name', title: 'Title', width: '30%' },
              {
                field: 'realm.name',
                title: 'Location',
                width: '25%',
                hideBelow: 'md',
                render: (row) => {
                  const name = get(row, 'realm.name');
                  return (
                    <FlexGridIconLabel icon={getRealmIcon(row.realm)} title={name}>
                      {name}
                    </FlexGridIconLabel>
                  );
                },
              },
              { field: 'authors.0.name', title: 'Author', width: '18%', hideBelow: 'md' },
              {
                field: 'status',
                title: 'Global',
                width: '10%',
                hideBelow: 'sm',
                render: (row) => (row.status === 'global_template' ? 'Yes' : 'No'),
              },
              {
                field: 'createLink',
                title: 'Link',
                width: '12%',
                minWidth: 40,
                cellSx: { overflow: 'visible' },
                render: (row) => (
                  <CopyLinkButton
                    templateId={get(row, 'id')}
                    onCopied={(message) => setAlerts({ message })}
                  />
                ),
              },
            ]}
            onSelectRow={selectRow}
          />
          <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
        </>
      )}
    </AdminContainer>
  );
});
